import * as m from "@paraglide/messages"
import { PlaydataButtonAnchor, PlaydataButtonLink, PlaydataSection } from "@playdata/PlaydataPrimitives"
import { usePlaydataSampleReportUrl } from "@playdata/playdata-settings-context"
import type { SupportedLocale } from "@site-types/content"
import { createFileRoute } from "@tanstack/react-router"
import { playdataRouteHead } from "@utils/playdata-route-head"

export const Route = createFileRoute("/$locale/playdata/pricing")({
  head: ({ params }) => {
    const locale = params.locale as SupportedLocale
    return playdataRouteHead({
      description: m.playdata_pricing_meta_description({}, { locale }),
      locale,
      path: "/playdata/pricing",
      title: m.playdata_pricing_meta_title({}, { locale }),
    })
  },
  component: RouteComponent,
})

/** A section title in the live style: dark text in a soft white pill on the wave. */
function CategoryTitle({ children }: { children: string }) {
  return (
    <h2 className="mx-auto w-fit bg-play-white/80 px-[2.4rem] py-[0.4rem] text-center text-[2.4rem] shadow-[0_0_24px_rgba(255,255,255,0.9)] md:text-[2.8rem]">
      <strong>{children}</strong>
    </h2>
  )
}

function RouteComponent() {
  const { locale: raw } = Route.useParams()
  const locale = raw as SupportedLocale
  const sampleReportUrl = usePlaydataSampleReportUrl(locale)

  const features = [
    m.playdata_pricing_feature1({}, { locale }),
    m.playdata_pricing_feature2({}, { locale }),
    m.playdata_pricing_feature3({}, { locale }),
    m.playdata_pricing_feature4({}, { locale }),
    m.playdata_pricing_feature5({}, { locale }),
    m.playdata_pricing_feature6({}, { locale }),
    m.playdata_pricing_feature7({}, { locale }),
    m.playdata_pricing_feature8({}, { locale }),
  ]

  const plans = [
    {
      footnote: m.playdata_pricing_basic_footnote({}, { locale }),
      included: [
        m.playdata_pricing_basic_included1({}, { locale }),
        m.playdata_pricing_basic_included2({}, { locale }),
      ],
      key: "basic",
      name: m.playdata_pricing_basic_name({}, { locale }),
      price: m.playdata_pricing_basic_price({}, { locale }),
      summary: m.playdata_pricing_basic_summary({}, { locale }),
    },
    {
      footnote: m.playdata_pricing_league_footnote({}, { locale }),
      included: [
        m.playdata_pricing_league_included1({}, { locale }),
        m.playdata_pricing_league_included2({}, { locale }),
      ],
      key: "league",
      name: m.playdata_pricing_league_name({}, { locale }),
      price: m.playdata_pricing_league_price({}, { locale }),
      summary: m.playdata_pricing_league_summary({}, { locale }),
    },
  ]

  return (
    <PlaydataSection tone="wave">
      <h1 className="mb-[4rem] text-center text-[2rem]">
        <strong>{m.playdata_pricing_heading({}, { locale })}</strong>
      </h1>

      <CategoryTitle>{m.playdata_pricing_categoryEvent({}, { locale })}</CategoryTitle>

      <div className="mx-auto mt-[4rem] grid max-w-[1000px] gap-[4rem] md:grid-cols-2">
        {plans.map((plan) => (
          <div
            className="flex flex-col gap-[1.6rem]"
            key={plan.key}
          >
            <article className="flex flex-col gap-[2rem] bg-play-white px-[2.8rem] py-[2.8rem] shadow-[0_2px_12px_rgba(0,0,0,0.06)]">
              <div className="flex flex-col items-center gap-[0.6rem] text-center">
                <h3 className="text-[1.6rem] uppercase tracking-[0.04em]">
                  <strong>{plan.name}</strong>
                </h3>
                <p className="text-[1.4rem]">{plan.summary}</p>
              </div>

              <p className="flex items-baseline justify-center gap-[0.4rem]">
                <span className="text-[1.6rem]">¥</span>
                <span className="font-play-display text-[3.6rem] tabular-nums leading-none">
                  <strong>{plan.price.replace(/^¥/, "")}</strong>
                </span>
                <span className="self-end text-[1.2rem]">{m.playdata_pricing_taxNote({}, { locale })}</span>
              </p>

              <div className="flex flex-col gap-[1.2rem]">
                <h4 className="border-play-green border-b-2 pb-[0.4rem] text-[1.7rem]">
                  <strong>{m.playdata_pricing_contentsLabel({}, { locale })}</strong>
                </h4>
                <ul className="flex list-[circle] flex-col gap-[0.6rem] pl-[2rem] text-[1.5rem] leading-[1.6]">
                  {plan.included.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                  {features.map((feature) => (
                    <li key={feature}>{feature}</li>
                  ))}
                </ul>
              </div>

              <div className="mt-[0.8rem] flex flex-col gap-[1rem]">
                <PlaydataButtonAnchor
                  href={sampleReportUrl}
                  rel="noopener"
                  target="_blank"
                >
                  {m.playdata_common_sampleCta({}, { locale })}
                </PlaydataButtonAnchor>
                <PlaydataButtonLink
                  params={{ locale }}
                  to="/$locale/contact"
                  variant="outline"
                >
                  {m.playdata_pricing_purchaseCta({}, { locale })}
                </PlaydataButtonLink>
              </div>
            </article>
            <p className="text-[1.2rem] leading-[1.6]">※{plan.footnote}</p>
          </div>
        ))}
      </div>

      <div className="mx-auto mt-[5rem] flex max-w-[60ch] flex-col gap-[0.6rem] text-[1.5rem] leading-[1.7]">
        <p>
          <strong>{m.playdata_pricing_example_heading({}, { locale })}</strong>
        </p>
        <p>
          <strong>{m.playdata_pricing_example_formula({}, { locale })}</strong>
        </p>
        <p>{m.playdata_pricing_example_note({}, { locale })}</p>
      </div>

      <div className="mt-[7rem] flex flex-col items-center gap-[2.4rem]">
        <CategoryTitle>{m.playdata_pricing_categoryStreamer({}, { locale })}</CategoryTitle>
        <p className="text-[1.5rem]">{m.playdata_pricing_comingSoon({}, { locale })}</p>
      </div>

      <div className="mt-[6rem] flex flex-col items-center gap-[2.4rem]">
        <CategoryTitle>{m.playdata_pricing_categoryIndustry({}, { locale })}</CategoryTitle>
        <p className="text-[1.5rem]">{m.playdata_pricing_comingSoon({}, { locale })}</p>
      </div>
    </PlaydataSection>
  )
}
